import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Star, Download, MessageSquare } from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Extension } from '../types/index';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useStorageFiles } from '../hooks/useStorageFiles';
import Navbar from '../components/Navbar';

function getFileNameFromUrl(url: string): string {
  // Remove query parameters
  const urlWithoutParams = url.split('?')[0];
  // Get the last part of the path
  const parts = urlWithoutParams.split('/');
  return parts[parts.length - 1];
}

function Home() {
  const { loading: authLoading } = useAuth();
  const { extensions: storageExtensions, thumbnails, loading: storageLoading, error: storageError } = useStorageFiles();
  const [searchParams, setSearchParams] = useSearchParams();
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filteredExtensionsList, setFilteredExtensionsList] = useState<Extension[]>([]);

  useEffect(() => {
    // Update search query when URL parameters change
    const search = searchParams.get('search');
    if (search !== null) {
      setSearchQuery(search);
    }
  }, [searchParams]);

  useEffect(() => {
    // Update filtered extensions whenever extensions, searchQuery, or selectedCategory changes
    const filtered = extensions.filter(ext => {
      // First filter by category if not 'all'
      if (selectedCategory !== 'all' && ext.categoryId !== selectedCategory) {
        return false;
      }
      
      // Then filter by search query if it exists
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        return (
          ext.name.toLowerCase().includes(searchLower) ||
          ext.description.toLowerCase().includes(searchLower) ||
          ext.author.toLowerCase().includes(searchLower) ||
          ext.category.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
    setFilteredExtensionsList(filtered);
  }, [extensions, searchQuery, selectedCategory]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const categoriesRef = collection(db, 'categories');
        const extensionsRef = collection(db, 'extensions');
        
        const [categoriesSnapshot, extensionsSnapshot] = await Promise.all([
          getDocs(query(categoriesRef)),
          getDocs(query(extensionsRef, orderBy('createdAt', 'desc')))
        ]);

        const categoriesList: {id: string, name: string}[] = [];
        const extensionsList: Extension[] = [];

        categoriesSnapshot.forEach((doc) => {
          categoriesList.push({
            id: doc.id,
            name: doc.data().name
          });
        });

        extensionsSnapshot.forEach((doc) => {
          const data = doc.data();
          const category = categoriesList.find(cat => cat.id === data.categoryId);
          
          const originalExtensionName = getFileNameFromUrl(data.downloadUrl);
          const originalThumbnailName = getFileNameFromUrl(data.thumbnailUrl);
          
          const formattedName = data.name.toLowerCase().replace(/\s+/g, '-');
          const possibleExtensionNames = [
            originalExtensionName,
            `${formattedName}-v${data.version}.js`,
            `${formattedName}.js`,
            data.name + '.js',
            data.name.replace(/\s+/g, '') + '.js'
          ];

          const possibleThumbnailNames = [
            originalThumbnailName,
            `${formattedName}-v${data.version}-thumbnail.png`,
            `${formattedName}-thumbnail.png`,
            `${formattedName}.png`,
            data.name.replace(/\s+/g, '') + '.png',
            data.name + '.png'
          ];
          
          const storageExtension = storageExtensions.find(se => 
            possibleExtensionNames.some(name => name === se.name)
          );
          
          const storageThumbnail = thumbnails.find(st => 
            possibleThumbnailNames.some(name => name === st.name)
          );
          
          extensionsList.push({
            id: doc.id,
            name: data.name,
            description: data.description,
            version: data.version,
            author: data.author,
            downloadUrl: storageExtension?.downloadUrl || data.downloadUrl,
            thumbnailUrl: storageThumbnail?.downloadUrl || data.thumbnailUrl,
            categoryId: data.categoryId,
            category: category?.name || 'Uncategorized',
            downloads: data.downloads || 0,
            totalRatings: data.totalRatings || 0,
            totalRatingSum: data.totalRatingSum || 0,
            averageRating: data.averageRating || 0,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });

        setCategories(categoriesList);
        setExtensions(extensionsList);
      } catch (error) {
        toast.error('Failed to fetch extensions');
      } finally {
        setLoading(false);
      }
    }

    if (!authLoading && !storageLoading) {
      fetchData();
    }
  }, [authLoading, storageLoading, storageExtensions, thumbnails]);

  useEffect(() => {
    if (storageError) {
      toast.error('Failed to load files from storage');
    }
  }, [storageError]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // Update URL with search parameter
    setSearchParams(query ? { search: query } : {});
  };

  return (
    <>
      <Navbar onSearch={handleSearch} />
      <div className="container mx-auto px-4 py-8">
        {/* Category Filters */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              All Extensions
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Extensions Grid */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading || storageLoading ? (
            <div className="text-center py-12 col-span-full">Loading extensions...</div>
          ) : filteredExtensionsList.length > 0 ? (
            filteredExtensionsList.map((extension) => (
              <Link
                key={extension.id}
                to={`/extension/${extension.id}`}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden"
              >
                {extension.thumbnailUrl ? (
                  <img
                    src={extension.thumbnailUrl}
                    alt={`${extension.name} thumbnail`}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = '/placeholder-thumbnail.png';
                    }}
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">No thumbnail available</span>
                  </div>
                )}
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {extension.name}
                    </h3>
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">
                      {extension.category}
                    </span>
                  </div>
                  <p className="text-gray-600 line-clamp-1">
                    {extension.description}
                  </p>
                  <div className="flex justify-between items-center pt-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Star className="w-5 h-5 text-yellow-400 fill-current" />
                        <span className="text-gray-600">
                          {(extension.averageRating || 0).toFixed(1)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MessageSquare className="w-5 h-5 text-gray-600" />
                        <span className="text-gray-600">
                          {extension.totalRatings || 0}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-600">
                      <Download className="w-5 h-5" />
                      <span>{extension.downloads || 0}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="text-center py-12 col-span-full">
              {searchQuery ? 'No extensions found matching your search' : 'No extensions found'}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default Home;
